import type { KeyboardEvent } from "react";
import type { DashboardRow } from "../../services/dashboard-api";

const STATUS_LABEL: Record<string, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  submitted: "Submitted",
  pending_grading: "Pending grading",
  completed: "Completed",
};

/** FR-017/FR-018: the standardized per-member results table, driven entirely by props. Clicking
 * a row is optional (`onSelectMember`) — the Manager's own dashboard wires it to the per-member
 * drill-down; a CO Member viewing another company's dashboard may choose not to. */
export function ResultsTable({
  rows,
  onSelectMember,
}: {
  rows: DashboardRow[];
  onSelectMember?: (memberId: string) => void;
}) {
  return (
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="border-b border-surface-alt text-body">
          <th className="py-2">Name</th>
          <th>Department</th>
          <th>Status</th>
          <th>Score</th>
          <th>Pass/Fail</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.memberId}
            className={`border-b border-surface-alt ${onSelectMember ? "cursor-pointer hover:bg-surface-alt" : ""}`}
            onClick={() => onSelectMember?.(r.memberId)}
            {...(onSelectMember
              ? {
                  tabIndex: 0,
                  role: "button",
                  "aria-label": `View ${r.fullName}'s result`,
                  onKeyDown: (e: KeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectMember(r.memberId);
                    }
                  },
                }
              : {})}
          >
            <td className="py-2 text-ink">{r.fullName}</td>
            <td>{r.department ?? "Unassigned"}</td>
            <td>{STATUS_LABEL[r.status] ?? r.status}</td>
            <td>{r.scorePercent !== null ? `${r.scorePercent}%` : "—"}</td>
            <td>{r.passed === null ? "—" : r.passed ? "Pass" : "Fail"}</td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={5} className="py-6 text-center text-body">
              No members match the current filter.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
